import { useEffect, useState } from "react";
import { ChevronDown, Menu, X } from "lucide-react";
import { LocaleLink } from "./LocaleLink";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { ThemeToggle } from "./ThemeToggle";
import { Logo } from "./Logo";
import { useT } from "@/lib/i18n-dict";
import { ABOUT_NAV, FEATURE_NAV, INDUSTRY_NAV, RESOURCE_NAV, SITE } from "@/content/site";

function DesktopGroup({ label, items }: { label: string; items: { label: string; to: string }[] }) {
  const t = useT();
  return (
    <div className="group relative">
      <button
        type="button"
        className="inline-flex items-center gap-1 px-1 py-2 text-sm text-muted-foreground transition-colors group-hover:text-foreground"
      >
        {t(label)}
        <ChevronDown className="size-3.5" aria-hidden />
      </button>
      <div className="invisible absolute start-0 top-full z-50 min-w-56 translate-y-1 rounded-lg border border-border bg-popover p-2 opacity-0 shadow-[var(--shadow-lift)] transition-all group-hover:visible group-hover:translate-y-0 group-hover:opacity-100">
        {items.map((item) => (
          <LocaleLink
            key={item.to}
            to={item.to}
            className="block rounded-md px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          >
            {t(item.label)}
          </LocaleLink>
        ))}
      </div>
    </div>
  );
}

type NavGroup = { label: string; items: { label: string; to: string }[] };

export function SiteHeader() {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const mobileGroups: NavGroup[] = [
    { label: "Industries", items: INDUSTRY_NAV },
    { label: "Features", items: FEATURE_NAV },
    { label: "Resources", items: RESOURCE_NAV },
    { label: "About us", items: ABOUT_NAV },
  ];
  const directLinks = [
    { label: "Solution finder", to: "/solution-finder" },
    { label: "Add-ons", to: "/addons" },
    { label: "Pricing", to: "/prices" },
  ];

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    if (!open) setOpenGroup(null);
  }, [open]);

  return (
    <header
      className={`${open ? "fixed inset-x-0 top-0" : "sticky top-0"} z-50 border-b border-border/70 bg-background/85 backdrop-blur-xl`}
    >
      <div className="container-page flex h-16 items-center justify-between gap-4">
        <LocaleLink to="/" aria-label="factory.online home" className="flex shrink-0 items-center">
          <Logo />
        </LocaleLink>

        <nav className="hidden items-center gap-6 lg:flex" aria-label="Main">
          <DesktopGroup label="Industries" items={INDUSTRY_NAV} />
          <DesktopGroup label="Features" items={[...FEATURE_NAV, { label: "Add-ons", to: "/addons" }]} />
          <DesktopGroup label="Resources" items={RESOURCE_NAV} />
          <LocaleLink
            to="/prices"
            className="px-1 py-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
          >
            {t("Pricing")}
          </LocaleLink>
          <DesktopGroup label="About us" items={ABOUT_NAV} />
        </nav>

        <div className="flex shrink-0 items-center gap-2">
          <ThemeToggle />
          <div className="hidden sm:block">
            <LanguageSwitcher />
          </div>
          <a
            href={SITE.appUrl}
            className="hidden rounded-md border border-border px-4 py-2 text-sm font-medium transition-colors hover:bg-accent lg:inline-flex"
          >
            {t("Log in")}
          </a>
          <LocaleLink
            to="/solution-finder"
            className="hidden rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90 lg:inline-flex"
          >
            {t("Solution finder")}
          </LocaleLink>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? t("Close menu") : t("Open menu")}
            aria-expanded={open}
            className="inline-flex size-10 items-center justify-center rounded-md border border-border lg:hidden"
          >
            {open ? <X className="size-5" /> : <Menu className="size-5" />}
          </button>
        </div>
      </div>

      {open ? (
        <div className="absolute inset-x-0 top-full z-40 flex h-[calc(100dvh-4rem)] flex-col overflow-y-auto overscroll-contain border-t border-border bg-background lg:hidden">
          <div className="container-page flex items-center justify-between gap-3 py-4 sm:hidden">
            <LanguageSwitcher />
            <ThemeToggle />
          </div>
          <nav className="container-page grid gap-1 pb-4" aria-label="Mobile">
            {mobileGroups.map((group) => {
              const expanded = openGroup === group.label;
              return (
                <div key={group.label} className="border-b border-border/50">
                  <button
                    type="button"
                    onClick={() => setOpenGroup(expanded ? null : group.label)}
                    aria-expanded={expanded}
                    className="flex min-h-12 w-full items-center justify-between rounded-md px-3 text-base font-semibold text-foreground transition-colors hover:bg-accent"
                  >
                    {t(group.label)}
                    <ChevronDown
                      className={`size-4 text-muted-foreground transition-transform duration-200 ${expanded ? "rotate-180" : ""}`}
                      aria-hidden
                    />
                  </button>
                  <div
                    className={`grid transition-[grid-template-rows,opacity] duration-200 ease-out ${
                      expanded ? "[grid-template-rows:1fr] opacity-100" : "[grid-template-rows:0fr] opacity-0"
                    }`}
                  >
                    <div className="overflow-hidden">
                      <div className="grid gap-0.5 border-s-2 border-primary/40 pb-2 ps-2 ms-3">
                        {group.items.map((item) => (
                          <LocaleLink
                            key={item.to}
                            to={item.to}
                            onClick={() => setOpen(false)}
                            className="flex min-h-11 items-center truncate rounded-md px-3 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                          >
                            {t(item.label)}
                          </LocaleLink>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            {directLinks.map((link) => (
              <LocaleLink
                key={link.to}
                to={link.to}
                onClick={() => setOpen(false)}
                className="flex min-h-12 items-center rounded-md border-b border-border/50 px-3 text-base font-semibold text-foreground transition-colors hover:bg-accent"
              >
                {t(link.label)}
              </LocaleLink>
            ))}
          </nav>
          <div className="sticky bottom-0 mt-auto border-t border-border bg-background/95 backdrop-blur-xl">
            <div className="container-page grid grid-cols-2 gap-2 py-4">
              <a
                href={SITE.appUrl}
                className="rounded-md border border-border px-4 py-3 text-center text-sm font-medium"
              >
                {t("Log in")}
              </a>
              <LocaleLink
                to="/registration-demo"
                onClick={() => setOpen(false)}
                className="rounded-md bg-primary px-4 py-3 text-center text-sm font-semibold text-primary-foreground"
              >
                {t("Book a demo")}
              </LocaleLink>
            </div>
          </div>
        </div>
      ) : null}
    </header>
  );
}
